/**
 * Routing without a router library: the path is the state.
 *
 * The site has six page kinds, each a function of the path, so a route is a
 * small discriminated union parsed from `location.pathname`, and navigation is
 * `history.pushState` plus one event the application listens to. The charter
 * names React and Tailwind for this part and nothing else, and a router would
 * be a dependency for six patterns. The identifier path `/id/` is not a
 * route here on purpose: the edge answers it with a redirect before the
 * application loads, and the local server does the same.
 */
import { useEffect, useState } from 'react';
import { reportView } from './views.js';

export type Route =
  | { readonly kind: 'home' }
  | { readonly kind: 'organization'; readonly ein: string }
  | { readonly kind: 'funder'; readonly ein: string }
  | { readonly kind: 'subject'; readonly slug: string }
  | { readonly kind: 'coalition'; readonly ein: string }
  | { readonly kind: 'coalition-cluster'; readonly window: string; readonly hash: string }
  | { readonly kind: 'about'; readonly slug: string | null }
  | { readonly kind: 'not-found'; readonly path: string };

export const routeFor = (path: string): Route => {
  const segments = path.split('/').filter((segment) => segment.length > 0);
  const [first, second, third, fourth] = segments;
  if (first === undefined) {
    return { kind: 'home' };
  }
  if (first === 'org' && second !== undefined && third === undefined) {
    return { kind: 'organization', ein: second };
  }
  if (first === 'funder' && second !== undefined && third === undefined) {
    return { kind: 'funder', ein: second };
  }
  if (first === 'subject' && second !== undefined && third === undefined) {
    return { kind: 'subject', slug: second };
  }
  if (first === 'coalition' && second === 'cluster' && third !== undefined && fourth !== undefined) {
    return { kind: 'coalition-cluster', window: third, hash: fourth };
  }
  if (first === 'coalition' && second !== undefined && third === undefined) {
    return { kind: 'coalition', ein: second };
  }
  if (first === 'about' && third === undefined) {
    return { kind: 'about', slug: second ?? null };
  }
  return { kind: 'not-found', path };
};

const NAVIGATED = 'fundergraph:navigated';

export const navigate = (path: string): void => {
  history.pushState(null, '', path);
  window.dispatchEvent(new Event(NAVIGATED));
  reportView(location.pathname);
};

export const useRoute = (): Route => {
  const [route, setRoute] = useState<Route>(() => routeFor(location.pathname));
  useEffect(() => {
    // The first page is reported here, once; later pages are reported by navigate, and a back or forward step by popstate.
    reportView(location.pathname);
    const update = (): void => {
      setRoute(routeFor(location.pathname));
      window.scrollTo(0, 0);
    };
    const back = (): void => {
      update();
      reportView(location.pathname);
    };
    window.addEventListener('popstate', back);
    window.addEventListener(NAVIGATED, update);
    return () => {
      window.removeEventListener('popstate', back);
      window.removeEventListener(NAVIGATED, update);
    };
  }, []);
  return route;
};

/** Click handling for an in-site link: same-origin paths navigate in place; modified clicks keep the browser's behaviour. */
export const onLinkClick = (path: string, before?: () => void) => (event: React.MouseEvent<HTMLAnchorElement>) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  event.preventDefault();
  before?.();
  navigate(path);
};
